import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '../utils/toast';
import { ArrowRight, Building2, ClipboardList, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReassignmentModal = ({ isOpen, onClose, sourceUser, type, onComplete }) => {
  const [items, setItems] = useState([]);
  const [targetUsers, setTargetUsers] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [targetUserId, setTargetUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, sourceUser, type]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch items (leads or hospitals)
      const itemsEndpoint = type === 'leads' 
        ? `/crm/users/${sourceUser.id}/leads`
        : `/crm/users/${sourceUser.id}/hospitals`;
      
      const itemsResponse = await axios.get(`${API}${itemsEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setItems(itemsResponse.data);
      
      // Fetch available target users (active CRM Officers and Associates, excluding source user)
      const usersResponse = await axios.get(`${API}/crm/users/all?include_deactivated=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const availableUsers = usersResponse.data.filter(u => u.id !== sourceUser.id && u.is_active);
      setTargetUsers(availableUsers);
    } catch (error) {
      toast.error(`Failed to load ${type}`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedItems(items.map(item => item.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleItemSelect = (itemId, checked) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems.filter(id => id !== itemId));
      setSelectAll(false);
    }
  };

  const handleReassign = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to reassign');
      return;
    }
    
    if (!targetUserId) {
      toast.error('Please select a target user');
      return;
    }
    
    const targetUser = targetUsers.find(u => u.id === targetUserId);
    const confirmMessage = `Are you sure you want to reassign ${selectedItems.length} ${type} from ${sourceUser.full_name} to ${targetUser.full_name}?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const endpoint = type === 'leads' ? '/crm/leads/reassign' : '/crm/hospitals/reassign';
      const payload = type === 'leads'
        ? { lead_ids: selectedItems, target_user_id: targetUserId }
        : { hospital_ids: selectedItems, target_user_id: targetUserId };
      
      const response = await axios.post(`${API}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to reassign ${type}`);
    } finally {
      setSubmitting(false);
    }
  };

  const getItemDisplayName = (item) => {
    if (type === 'leads') {
      return item.hospital_name;
    } else {
      return item.institution_name || item.hospital_name || 'Unnamed Hospital';
    }
  };

  const getItemDetails = (item) => {
    if (type === 'leads') {
      return `${item.city} • ${item.disposition_status}`;
    } else {
      return item.city || 'Location not specified';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {type === 'leads' ? (
              <ClipboardList className="w-5 h-5 text-blue-600" />
            ) : (
              <Building2 className="w-5 h-5 text-green-600" />
            )}
            <span>Reassign {type === 'leads' ? 'Leads' : 'Hospitals'}</span>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading {type}...</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Source and Target Selection */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                  <div className="bg-white rounded-lg p-3 border border-gray-300">
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <div>
                        <div className="font-medium text-gray-900">{sourceUser.full_name}</div>
                        <div className="text-xs text-gray-500">{sourceUser.role}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <ArrowRight className="w-6 h-6 text-gray-400" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <Select value={targetUserId} onValueChange={setTargetUserId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select target user" />
                    </SelectTrigger>
                    <SelectContent>
                      {targetUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{user.full_name}</span>
                            <span className="text-xs text-gray-500">({user.role})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Items List */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={selectAll}
                    onCheckedChange={handleSelectAll}
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Select All ({items.length} items)
                  </label>
                </div>
                <div className="text-sm text-gray-600">
                  {selectedItems.length} selected
                </div>
              </div>

              <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg">
                {items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No {type} found for this user
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {items.map((item) => (
                      <div
                        key={item.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleItemSelect(item.id, !selectedItems.includes(item.id))}
                      >
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            checked={selectedItems.includes(item.id)}
                            onCheckedChange={(checked) => handleItemSelect(item.id, checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {getItemDisplayName(item)}
                            </div>
                            <div className="text-sm text-gray-500 mt-1">
                              {getItemDetails(item)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReassign}
                disabled={submitting || selectedItems.length === 0 || !targetUserId}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Reassigning...
                  </>
                ) : (
                  <>Reassign {selectedItems.length} {type}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReassignmentModal;
